/**
 * TimePickerEditor
 * Configuration editor for time-picker control
 * Uses CONDITIONAL PATTERN: different config for 12h vs 24h format
 */

import React, { useCallback } from 'react';
import type { PropertyEditor, PropertyEditorProps } from '../../../../registry/PropertyEditor.types';

interface TimePickerConfig {
  format?: '12h' | '24h';
  step?: number;
  minTime?: string;
  maxTime?: string;
  defaultTime?: string;
}

function renderTimePickerEditor(props: PropertyEditorProps) {
  const { control, onConfigChange } = props;
  const config = (control.config || {}) as TimePickerConfig;
  const format = config.format || '24h';
  const step = config.step || 15;

  const handleFormatChange = useCallback(
    (newFormat: '12h' | '24h') => {
      onConfigChange('format', newFormat);
    },
    [onConfigChange],
  );

  const handleStepChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      onConfigChange('step', Number(e.target.value));
    },
    [onConfigChange],
  );

  const handleMinTimeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onConfigChange('minTime', e.target.value);
    },
    [onConfigChange],
  );

  const handleMaxTimeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onConfigChange('maxTime', e.target.value);
    },
    [onConfigChange],
  );

  const handleDefaultTimeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onConfigChange('defaultTime', e.target.value);
    },
    [onConfigChange],
  );

  return (
    <div className="af-properties-tab__section">
      <h4 className="af-properties-tab__subsection-title">Time Picker Configuration</h4>

      <div className="af-properties-tab__group">
        <label className="af-properties-tab__label">Time Format</label>
        <div className="af-properties-tab__radio-group">
          <label className="af-properties-tab__radio-label">
            <input
              type="radio"
              value="24h"
              checked={format === '24h'}
              onChange={(e) => handleFormatChange(e.target.value as '24h')}
            />
            24-Hour Format
          </label>
          <label className="af-properties-tab__radio-label">
            <input
              type="radio"
              value="12h"
              checked={format === '12h'}
              onChange={(e) => handleFormatChange(e.target.value as '12h')}
            />
            12-Hour Format (AM/PM)
          </label>
        </div>
      </div>

      <div className="af-properties-tab__group">
        <label htmlFor="step-select" className="af-properties-tab__label">
          Time Step (minutes)
        </label>
        <select
          id="step-select"
          value={step}
          onChange={handleStepChange}
          className="af-properties-tab__select"
        >
          <option value="1">1 minute</option>
          <option value="5">5 minutes</option>
          <option value="15">15 minutes</option>
          <option value="30">30 minutes</option>
          <option value="60">1 hour</option>
        </select>
      </div>

      <div className="af-properties-tab__group">
        <label htmlFor="min-time-input" className="af-properties-tab__label">
          Minimum Time
        </label>
        <input
          id="min-time-input"
          type="time"
          value={config.minTime || ''}
          onChange={handleMinTimeChange}
          className="af-properties-tab__input"
          aria-label="Minimum time"
        />
      </div>

      <div className="af-properties-tab__group">
        <label htmlFor="max-time-input" className="af-properties-tab__label">
          Maximum Time
        </label>
        <input
          id="max-time-input"
          type="time"
          value={config.maxTime || ''}
          onChange={handleMaxTimeChange}
          className="af-properties-tab__input"
          aria-label="Maximum time"
        />
      </div>

      <div className="af-properties-tab__group">
        <label htmlFor="default-time-input" className="af-properties-tab__label">
          Default Time
        </label>
        <input
          id="default-time-input"
          type="time"
          value={config.defaultTime || ''}
          onChange={handleDefaultTimeChange}
          className="af-properties-tab__input"
          aria-label="Default time"
        />
      </div>
    </div>
  );
}

export const TimePickerEditor: PropertyEditor = {
  type: 'time-picker',
  label: 'Time Picker',
  description: 'Configure time picker with format, step, and min/max constraints',
  render: renderTimePickerEditor,
};
